import { Injectable } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import {
    Column,
    ColumnType,
} from '../../../../SGRE-shared/models/tableConfiguration';
import {
    ProductTableColumn,
    ProductTableValue,
    QuantityResult,
    SubmitProductCode,
    SubmitTableDataState
} from '../../models/bulk-order.model';
import { BulkOrderService } from '../../services/bulk-order.service';
import { BulkOrderFetchState, ProductCode, TableDataState } from '../bulk-order.state';
import { ProductVariant } from '../../../product-details-page/models/product-info';
import { CustomVariantOption } from '../../../product-list-page/models/custom-product.model';

@Injectable()
export class BulKOrderEffectsService {

    constructor(
        private bulkOrderService: BulkOrderService
    ) { }

    public imageUrl: string = environment.siteUrls.getBackofficeUrl.slice(0, -1);

    getTableData(data: BulkOrderFetchState): BulkOrderFetchState {
        const updateDataFoundProduct = this.createTableConfigurationFoundProduct(data?.foundProductCodes || []);
        const updateDataNotFoundProduct = this.createTableConfigurationNotFoundProduct(data?.notFoundProductCodes as unknown as string[] || []);
        return {
            foundProductCodes: updateDataFoundProduct.foundProductsData,
            foundProductHeader: updateDataFoundProduct.columns,
            notFoundProductCodes: updateDataNotFoundProduct.notFoundProductsData,
            notFoundProductHeader: updateDataNotFoundProduct.columns
        } as BulkOrderFetchState;
    }

    createTableConfigurationFoundProduct(data: ProductCode[]) {
        const columnData = [
            { label: ProductTableColumn.IMAGE, name: ProductTableValue.IMAGE, type: ColumnType.image },
            { label: ProductTableColumn.PRODUCT_NAME, name: ProductTableValue.PRODUCT_NAME, type: ColumnType.link2 },
            { label: ProductTableColumn.PART_NUMBER, name: ProductTableValue.PART_NUMBER, type: ColumnType.link2 },
            { label: ProductTableColumn.REFURBISHED, name: ProductTableValue.REFURBISHED, type: ColumnType.checkbox2 },
            { label: ProductTableColumn.QUANTITY, name: ProductTableValue.QUANTITY, type: ColumnType.quantity },
        ];
        const columns = columnData.map((item) => new Column(item));
        const foundProductsData = data.map((item: ProductCode) => this.mapBulkOrderFoundData(item)) || [];
        return { foundProductsData, columns }
    }

    createTableConfigurationNotFoundProduct(data: string[]) {
        const columnData = [
            { label: ProductTableColumn.PART_NUMBER, name: ProductTableValue.PART_NUMBER, type: ColumnType.link },
            { label: ProductTableColumn.QUANTITY, name: ProductTableValue.QUANTITY, type: ColumnType.number },
        ];
        const columns = columnData.map(item => new Column(item));
        const notFoundProductsData = data.map((item: string) => this.mapBulkOrderNotFoundData(item)) || [];
        return { notFoundProductsData, columns }
    }

    mapBulkOrderFoundData(foundItem: ProductCode): ProductCode {
        let quantity = 0;
        let excelData: TableDataState[] = [];
        this.bulkOrderService.getExcelUploadedData().subscribe(data => { excelData = data })
        for (const row of excelData) {
            if (foundItem.code.toUpperCase().trim() === (row.partNumber.toUpperCase().trim())) {
                quantity = row.quantity;
                break;
            }
        }

        const variantOptions: CustomVariantOption[] = (foundItem.baseOptions?.length || 0) > 0
            ? foundItem.baseOptions?.at(0)?.options || []
            : foundItem.variantOptions || [];
        const newVariant = variantOptions.find((val) =>
            val.code?.endsWith(ProductVariant.NEW)
        ) || null;
        const refurbishedVariant = variantOptions.find((val) =>
            val.code?.endsWith(ProductVariant.REFURBISHED)
        ) || null;
        const variantObj: CustomVariantOption | null = newVariant || refurbishedVariant || null;
        const hasNewVariant = !!newVariant;
        const hasRefurbishedVariant = !!refurbishedVariant;
        const isRefurbishedOnly = hasRefurbishedVariant && !hasNewVariant;
        const minQuantity = this.bulkOrderService.getUpdatedMinQty(foundItem?.minQuantity, foundItem.interval);

        const obj = this.checkQuantity(
            quantity,
            minQuantity,
            variantObj?.maxQuantity || 1000,
            foundItem?.interval || 1
        );
        const newTableFoundData = { ...foundItem };
        newTableFoundData.imageUrl = foundItem?.images?.length
            ? this.imageUrl + foundItem?.images[0]?.url
            : '/images_and_icons/image1.jpg';
        newTableFoundData.partNumber = foundItem?.code;
        newTableFoundData.productName = foundItem?.name;
        newTableFoundData.refurbishedDisabled = !hasRefurbishedVariant || isRefurbishedOnly;
        newTableFoundData.qtyUpdatedFlag = obj.updated;
        newTableFoundData.quantity = Number(obj.quantity);
        newTableFoundData.url = foundItem.url;
        newTableFoundData.navigateUrl = foundItem.url;
        newTableFoundData.isObsolete = Boolean(variantObj?.obsoleteFlag);
        newTableFoundData.step = Number(foundItem.interval > 0 ? foundItem.interval : 1);
        newTableFoundData.maxQuantity = Number(variantObj?.maxQuantity ? variantObj?.maxQuantity : 1000);
        newTableFoundData.minQuantity = minQuantity;
        newTableFoundData.refurbished = isRefurbishedOnly;
        newTableFoundData.defaultVariantCode = newVariant?.code;
        newTableFoundData.refurbishedVariantCode = refurbishedVariant?.code;

        return newTableFoundData;
    }

    checkQuantity(quantity: number, minQuantity = 1, maxQuantity = 1000, interval = 1): QuantityResult {
        let qty: number = quantity;
        if (quantity < minQuantity) {
            qty = minQuantity
        } else if (quantity > maxQuantity) {
            qty = maxQuantity;
        } else if (quantity < interval) {
            qty = interval;
        } else if (quantity % interval !== 0) {
            qty = Math.round(quantity / interval) * interval;
        } else {
            qty = quantity
        }
        return { quantity: qty, updated: qty !== quantity };
    }

    mapBulkOrderNotFoundData(notFoundData: string): TableDataState {
        let quantity = 0;
        let excelData: TableDataState[] = [];
        this.bulkOrderService.getExcelUploadedData().subscribe(data => excelData = data)
        for (const row of excelData) {
            if (notFoundData.toUpperCase().trim() === (row.partNumber.toUpperCase().trim())) {
                quantity = row.quantity;
                break;
            }
        }
        const newTableFoundData: TableDataState = { // Use the tableDataState interface
            partNumber: notFoundData, // Access partNumber from the ProductCode object
            quantity: quantity  // Handle the case where quantity is not found
        };
        return newTableFoundData;
    }

    updateTableFoundData(tableData: BulkOrderFetchState): SubmitTableDataState {
        const mappedFoundResults: SubmitProductCode[] = [];
        const mappedNotFoundResults: string[] = [];
        tableData.foundProductCodes?.forEach((tableDataItem) => {
            let newFoundItem: SubmitProductCode;
            if (tableDataItem.refurbished && tableDataItem.refurbishedVariantCode) {
                newFoundItem = {
                    productCode: tableDataItem.refurbishedVariantCode,
                    count: tableDataItem.quantity,
                };
            } else {
                newFoundItem = {
                    productCode: tableDataItem.defaultVariantCode || tableDataItem.partNumber + (tableDataItem.refurbished ? '_R' : '_N'),
                    count: tableDataItem.quantity,
                };
            }
            mappedFoundResults.push(newFoundItem);
        });
        tableData.notFoundProductCodes?.forEach((tableDataItem) => {
            mappedNotFoundResults.push(tableDataItem.partNumber);
        });
        const foundTable: SubmitTableDataState = {
            foundProductCodes: mappedFoundResults,
            notFoundProductCodes: mappedNotFoundResults,
        }
        return foundTable
    }
}
